<template>
  <div class="mcontainer">
  <div class="event-admin">
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon-wrap">
          <Icon icon="ion:calendar-number-sharp" width="28" height="28" />
        </div>
        <div>
          <h1 class="page-title" style="color: white;">Kelola Events</h1>
          <p class="page-subtitle">Kelola dan pantau semua event alumni</p>
        </div>
      </div>
      <div class="header-actions">
        <div class="search-box">
          <Icon icon="mdi:magnify" width="20" height="20" class="search-icon" />
          <input
            class="search-input"
            v-model="search"
            @input="debouncedFetch"
            placeholder="Cari event..."
          />
        </div>
        <select class="filter-select" v-model="status" @change="applyStatus">
          <option value="">Semua Status</option>
          <option value="active">Aktif</option>
          <option value="inactive">Tidak Aktif</option>
        </select>
        <router-link to="/app/admin/events/create" class="btn-create">
          <Icon icon="mdi:plus" width="20" height="20" />
          <span>Buat Event</span>
        </router-link>
      </div>
    </div>

    <div class="card-panel">
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th class="col-thumb">Gambar</th>
              <th class="col-title">Judul</th>
              <th class="col-location">Lokasi</th>
              <th class="col-date">Tanggal</th>
              <th class="col-status">Status</th>
              <th class="col-actions">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="it in list" :key="it.id" class="data-row">
              <td>
                <div class="thumb-wrap">
                  <img v-if="it.image" :src="it.image" class="thumb" alt="">
                  <div v-else class="thumb placeholder">
                    <Icon icon="mdi:calendar-blank-outline" width="24" height="24" />
                  </div>
                </div>
              </td>
              <td>
                <div class="event-info">
                  <div class="event-title">{{ it.title }}</div>
                  <div class="event-desc">{{ truncate(it.description, 80) }}</div>
                </div>
              </td>
              <td>
                <span class="location-text">{{ it.location || '-' }}</span>
              </td>
              <td>
                <span class="date-text">{{ formatDate(it.event_date) }}</span>
              </td>
              <td>
                <span :class="['status-badge', it.status]">
                  {{ it.status === 'active' ? 'Aktif' : 'Tidak Aktif' }}
                </span>
              </td>
              <td>
                <div class="row-actions">
                  <router-link :to="{ name: 'EventAdminEdit', params: { id: it.id } }" class="action-btn action-edit" title="Edit">
                    <Icon icon="mdi:pencil" width="16" height="16" />
                    <span>Edit</span>
                  </router-link>
                  <button
                    :class="['action-btn', it.status === 'active' ? 'action-warning' : 'action-success']"
                    @click="toggleStatus(it.id)"
                    :disabled="loading"
                    :title="it.status === 'active' ? 'Nonaktifkan' : 'Aktifkan'"
                  >
                    <Icon :icon="it.status === 'active' ? 'mdi:pause' : 'mdi:play'" width="16" height="16" />
                    <span>{{ it.status === 'active' ? 'Nonaktifkan' : 'Aktifkan' }}</span>
                  </button>
                  <button
                    class="action-btn action-delete"
                    @click="destroy(it.id)"
                    :disabled="loading"
                    title="Hapus"
                  >
                    <Icon icon="mdi:trash-outline" width="16" height="16" />
                    <span>Hapus</span>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!loading && list.length === 0">
              <td colspan="6">
                <div class="empty-state">
                  <Icon icon="mdi:calendar-blank-outline" width="48" height="48" />
                  <p>Tidak ada event</p>
                  <router-link to="/app/admin/events/create" class="empty-link">Buat event pertama</router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="loading-overlay" v-if="loading">
        <div class="loading-spinner">
          <b-spinner />
        </div>
      </div>

      <div v-if="totalPage > 1" class="pagination-wrap">
        <span class="pagination-info">Halaman {{ page }} dari {{ totalPage }}</span>
        <div class="pagination-controls">
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="Pertama">
            <Icon icon="mdi:chevron-double-left" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Sebelumnya">
            <Icon icon="mdi:chevron-left" width="18" height="18" />
          </button>
          <button
            v-for="p in pages"
            :key="p"
            class="num-btn"
            :class="{ active: p === page }"
            :disabled="p === page || loading"
            @click="go(p)"
          >
            {{ p }}
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Selanjutnya">
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Terakhir">
            <Icon icon="mdi:chevron-double-right" width="18" height="18" />
          </button>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'EventAdmin',
  components: { Icon },
  data () {
    return {
      list: [],
      loading: false,
      page: 1,
      totalPage: 1,
      perPage: 10,
      status: '',
      search: '',
      debouncedFetch: null
    }
  },
  created () {
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed: {
    pages () {
      const t = this.totalPage
      const cur = this.page
      const arr = []
      const win = 5
      let s = Math.max(1, cur - 2)
      let e = Math.min(t, s + win - 1)
      if (e - s < win - 1) s = Math.max(1, e - win + 1)
      for (let i = s; i <= e; i++) arr.push(i)
      return arr
    }
  },
  methods: {
    truncate (s, n) { return s && s.length > n ? s.slice(0, n) + '...' : (s || '') },
    formatDate (d) {
      if (!d) return '-'
      const dt = new Date(d)
      return dt.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    debounce (fn, d) {
      let t = null
      return (...a) => {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, a), d)
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.set('page', this.page)
        params.set('per_page', this.perPage)
        if (this.status) params.set('status', this.status)
        if (this.search) params.set('search', this.search)
        const { data } = await axios.get(`/admin/events?${params.toString()}`)
        this.list = (data && data.data) ? data.data : []
        this.page = (data && data.current_page) ? data.current_page : 1
        this.totalPage = (data && data.last_page) ? data.last_page : 1
      } catch (e) {
        this.$swal({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Gagal memuat data' })
      } finally {
        this.loading = false
      }
    },
    go (p) {
      if (p < 1 || p > this.totalPage || p === this.page) return
      this.page = p
      this.fetchList()
    },
    applyStatus () {
      this.page = 1
      this.fetchList()
    },
    async toggleStatus (id) {
      try {
        await axios.post(`/admin/events/${id}/toggle-status`)
        this.fetchList()
      } catch (e) {
        this.$swal({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Gagal mengubah status' })
      }
    },
    async destroy (id) {
      if (!confirm('Yakin hapus event ini?')) return
      try {
        await axios.delete(`/admin/events/${id}`)
        this.fetchList()
      } catch (e) {
        this.$swal({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Gagal menghapus' })
      }
    }
  }
}
</script>

<style scoped>
.event-admin {
  padding: 0;
  max-width: 100%;
}

/* Header Section */
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  color: #fff;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.35);
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon-wrap {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  backdrop-filter: blur(8px);
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  min-width: 260px;
}

.search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 0 16px 0 44px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.search-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
}

.filter-select {
  height: 42px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-select option {
  background: #1e1b4b;
  color: #fff;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 20px;
  background: #fff;
  color: #667eea;
  font-weight: 600;
  font-size: 14px;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-create:hover {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Card Panel */
.card-panel {
  position: relative;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead th {
  background: #f8fafc;
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0;
}

.col-thumb { width: 100px; }
.col-title { min-width: 200px; }
.col-location { min-width: 120px; }
.col-date { min-width: 140px; }
.col-status { width: 100px; }
.col-actions { width: 240px; }

.data-row td {
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 14px;
  transition: background 0.15s ease;
}

.data-row:hover td {
  background: #f8fafc;
}

.thumb-wrap {
  display: flex;
  align-items: center;
}

.thumb {
  width: 80px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.thumb.placeholder {
  width: 80px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #94a3b8;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
}

.event-info {
  max-width: 280px;
}

.event-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.event-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.location-text,
.date-text {
  color: #475569;
  font-size: 13px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.status-badge.inactive {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.row-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s ease;
}

.action-edit {
  background: #dbeafe;
  color: #1d4ed8;
}

.action-edit:hover {
  background: #bfdbfe;
}

.action-success {
  background: #dcfce7;
  color: #166534;
}

.action-success:hover {
  background: #bbf7d0;
}

.action-warning {
  background: #fef3c7;
  color: #b45309;
}

.action-warning:hover {
  background: #fde68a;
}

.action-delete {
  background: #fee2e2;
  color: #b91c1c;
}

.action-delete:hover {
  background: #fecaca;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Empty State */
.empty-state {
  padding: 48px 24px;
  text-align: center;
  color: #94a3b8;
}

.empty-state .iconify {
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 500;
}

.empty-link {
  color: #667eea;
  font-weight: 600;
  text-decoration: none;
}

.empty-link:hover {
  text-decoration: underline;
}

/* Loading */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-spinner {
  color: #667eea;
}

/* Pagination */
.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-info {
  font-size: 13px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.15s ease;
}

.icon-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #334155;
}

.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.num-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}

.num-btn:hover:not(:disabled):not(.active) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.num-btn.active {
  background: #667eea;
  border-color: #667eea;
  color: #fff;
  cursor: default;
}

.num-btn:disabled {
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    padding: 16px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: 0;
  }

  .btn-create {
    justify-content: center;
  }

  .pagination-wrap {
    flex-direction: column;
    align-items: flex-start;
  }

  .row-actions {
    flex-direction: column;
  }

  .action-btn {
    justify-content: center;
  }
}
</style>
